import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { ConfiguratorModes } from "utils/enums";
import { T } from "services/lang/LanguageService";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { v4 as uuidv4 } from "uuid";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { textAligns, textSizes } from "utils/constants";
import useComponentWidthStore from "store/component-width-store";
import { getDefaultLayout } from "../layouts/helpers";
import ComponentTypeEditor from "./ComponentTypeEditor";
import ComponentEditor from "./ComponentEditor";
import ComponentThemeEditor from "./ComponentThemeEditor";

export const defaultComponent = (layoutType: DashboardLayoutType): IDashboardComponent => ({
  id: uuidv4(),
  title: T("word.new-component"),
  titleSize: "2xl",
  titleAlign: "left",
  type: "NONE",
  collapsable: false,
  isCollapsed: false,
  hideHeader: false,
  config: {},
  layout: getDefaultLayout(layoutType),
});

interface DashboardComponentConfiguratorNewProps {
  tabId: number;
  isDialogVisible: boolean;
  setIsDialogVisible: (isDialogVisible: boolean) => void;
  component: IDashboardComponent;
  onComponentChange?: (config: IDashboardComponent) => void;
  mode: ConfiguratorModes;
  layoutType: DashboardLayoutType;
  groupId?: string;
  isReports?: boolean;
}

const DashboardComponentConfiguratorNew = (props: DashboardComponentConfiguratorNewProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const setComponentWidth = useComponentWidthStore(state => state.setComponentWidth);
  const [currentConfig, setCurrentConfig] = useState<IDashboardComponent>(
    props.mode === ConfiguratorModes.ADD ? defaultComponent(props.layoutType) : props.component
  );
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent) => {
    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const handleWidthChange = (value: number) => {
    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      layout: {
        ...prevState.layout,
        [props.layoutType]: {
          ...prevState.layout[props.layoutType],
          width: value,
        },
      },
    }));
    setComponentWidth(value);
  };

  const handleCancelAddingComponent = () => {
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleAddComponentToCurrentTab = () => {
    const newComponent = {
      ...currentConfig,
      id: uuidv4(),
      layout: currentConfig.layout,
    };

    const newDashboard = {
      ...dashboard,
      dashboardTabsJs: dashboard?.dashboardTabsJs?.map((tab, index) => {
        if (index === props.tabId) {
          return {
            ...tab,
            componentGroups: tab.componentGroups.map(group =>
              group.id === props.groupId
                ? {
                    ...group,
                    components: [...(group.components || []), newComponent],
                  }
                : group
            ),
          };
        }
        return tab;
      }),
    } as Dashboard;
    setDashboard(newDashboard);
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleUpdateComponent = () => {
    const updatedComponent = {
      ...currentConfig,
      id: currentConfig.id || uuidv4(),
      layout: currentConfig.layout,
    };
    props.onComponentChange?.(updatedComponent);
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleCollapsableChange = (e: InputSwitchChangeEvent) => {
    if (!e.value) {
      setCurrentConfig((prevState: IDashboardComponent) => ({
        ...prevState,
        collapsable: e.value,
        isCollapsed: e.value,
      }));
    }
    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      collapsable: e.value,
    }));
  };

  return (
    <Dialog
      visible={props.isDialogVisible}
      onHide={() => props.setIsDialogVisible(false)}
      header={props.mode === ConfiguratorModes.ADD ? T("action.add-component") : T("action.edit-component")}
      footer={
        <div>
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            onClick={handleCancelAddingComponent}
            className="component-editor-button icon-red"
          />
          <Button
            label={props.mode === ConfiguratorModes.ADD ? T("action.add") : T("action.save")}
            icon="pi pi-plus"
            onClick={
              props.mode === ConfiguratorModes.ADD ? handleAddComponentToCurrentTab : handleUpdateComponent
            }
            className="component-editor-button icon-green"
          />
        </div>
      }
      style={{ width: "70rem" }}
    >
      <div className="py-0 px-3">
        <div className="p-fluid flex flex-row justify-content-between">
          <div className="p-col w-4">
            <label htmlFor="title">{T("word.title")}</label>
            <InputText
              id="title"
              type="text"
              value={currentConfig?.title ?? T("word.new-component")}
              onChange={handleTitleChange}
            />
          </div>
          <ComponentThemeEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
          <ComponentTypeEditor
            type={currentConfig.type}
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            isReports={props.isReports}
          />
        </div>
        <div className="flex flex-row flex-wrap justify-content-start">
          <div className="p-3 flex flex-column w-3">
            <label htmlFor="width">{T("word.width")}</label>
            <InputNumber
              inputId="width"
              min={2}
              max={12}
              value={currentConfig?.layout?.[props.layoutType]?.width ?? 6}
              onValueChange={e => handleWidthChange(e.value ?? 6)}
            />
          </div>
          <div className="p-3 flex flex-column w-12rem">
            <label htmlFor="titleSize">{T("word.title-size")}</label>
            <Dropdown
              id="titleSize"
              value={currentConfig?.titleSize ?? "2xl"}
              options={textSizes}
              onChange={handleTitleChange}
            />
          </div>
          <div className="p-3 flex flex-column w-14rem">
            <label htmlFor="titleAlign">{T("word.title-alignment")}</label>
            <Dropdown
              id="titleAlign"
              value={currentConfig?.titleAlign ?? "left"}
              options={textAligns}
              onChange={handleTitleChange}
            />
          </div>
          <div className="p-3 flex flex-column w-3 gap-2">
            <label htmlFor="hideHeader">{T("action.hide-component-header")}</label>
            <InputSwitch
              inputId="hideHeader"
              checked={currentConfig?.hideHeader ?? false}
              onChange={e =>
                setCurrentConfig((prevState: IDashboardComponent) => ({
                  ...prevState,
                  hideHeader: e.value,
                }))
              }
            />
          </div>
          <div className="p-3 flex flex-column w-3 gap-2">
            <label htmlFor="height">{T("label.collapsable")}</label>
            <InputSwitch
              inputId="collapsable"
              checked={currentConfig?.collapsable ?? false}
              onChange={handleCollapsableChange}
            />
          </div>
          {currentConfig?.collapsable && (
            <div className="p-3 flex flex-column w-3 gap-2">
              <label htmlFor="height">{T("label.collapsed")}</label>
              <InputSwitch
                inputId="collapsed"
                checked={currentConfig?.isCollapsed ?? false}
                onChange={e =>
                  setCurrentConfig((prevState: IDashboardComponent) => ({
                    ...prevState,
                    isCollapsed: e.value,
                  }))
                }
              />
            </div>
          )}
        </div>
        <ComponentEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
      </div>
    </Dialog>
  );
};

export default DashboardComponentConfiguratorNew;
